import { type Address, decodeFunctionData, formatUnits, type Hex, parseAbi } from 'viem'

// Common function selectors
export const FUNCTION_SELECTORS = {
    // ERC20
    TRANSFER: '0xa9059cbb',
    APPROVE: '0x095ea7b3',
    TRANSFER_FROM: '0x23b872dd',

    // ERC721
    SAFE_TRANSFER_FROM: '0x42842e0e',
    SAFE_TRANSFER_FROM_WITH_DATA: '0xb88d4fde',
    SET_APPROVAL_FOR_ALL: '0xa22cb465',

    // ERC1155
    SAFE_TRANSFER_FROM_1155: '0xf242432a',
    SAFE_BATCH_TRANSFER_FROM_1155: '0x2eb2c2d6',
} as const

// Token standards
export type TokenStandard = 'erc20' | 'erc721' | 'erc1155' | 'unknown'
export type TransactionType =
    | 'erc20-transfer'
    | 'erc20-approve'
    | 'erc20-transfer-from'
    | 'nft-transfer'
    | 'nft-approve'
    | 'nft-set-approval-all'
    | 'unknown'

export interface TokenInfo {
    address: Address
    symbol?: string
    name?: string
    decimals?: number
    logoUrl?: string
    amount?: bigint
    formattedAmount?: string
    tokenId?: bigint
}

export interface DecodedTransaction {
    type: TransactionType
    protocol?: 'erc20' | 'erc721' | 'erc1155'
    action: string // Human readable: "Transfer", "Approve"
    description?: string // Full human-readable description
    functionSignature?: string // Function signature like "approve(address,uint256)"
    tokens: TokenInfo[]
    recipient?: Address
    spender?: Address
    from?: Address
    data?: Hex // Additional data for functions like safeTransferFrom(with data)
    warnings?: string[]
    rawData?: {
        functionName?: string
        args?: readonly unknown[]
    }
}

// ABIs for common functions
const ERC20_ABI = parseAbi([
    'function transfer(address to, uint256 amount) returns (bool)',
    'function approve(address spender, uint256 amount) returns (bool)',
    'function transferFrom(address from, address to, uint256 amount) returns (bool)',
])

const ERC721_ABI = parseAbi([
    'function transferFrom(address from, address to, uint256 tokenId)',
    'function safeTransferFrom(address from, address to, uint256 tokenId)',
    'function safeTransferFrom(address from, address to, uint256 tokenId, bytes data)',
    'function approve(address to, uint256 tokenId)',
    'function setApprovalForAll(address operator, bool approved)',
])

const ERC1155_ABI = parseAbi([
    'function safeTransferFrom(address from, address to, uint256 id, uint256 amount, bytes data)',
    'function safeBatchTransferFrom(address from, address to, uint256[] ids, uint256[] amounts, bytes data)',
    'function setApprovalForAll(address operator, bool approved)',
])

/**
 * Get the function selector (first 4 bytes) from calldata
 */
export function getFunctionSelector(data: Hex): string {
    if (!data || data === '0x') {
        return ''
    }
    return data.slice(0, 10).toLowerCase() // First 10 chars (0x + 8 hex chars = 4 bytes)
}

/**
 * Check if an amount represents an unlimited approval
 */
export function isUnlimitedApproval(amount: bigint): boolean {
    // Max uint256
    const MAX_UINT256 = BigInt('0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff')
    // Consider it unlimited if it's max uint256 or very large (> 1e50)
    return (
        amount === MAX_UINT256 ||
        amount > BigInt('100000000000000000000000000000000000000000000000000')
    )
}

/**
 * Format token amount with decimals
 */
export function formatTokenAmount(amount: bigint, decimals: number = 18): string {
    const formatted = formatUnits(amount, decimals)
    // Remove trailing zeros and decimal point if whole number
    const cleaned = parseFloat(formatted).toString()
    return cleaned
}

/**
 * Decode ERC20 transactions
 */
function decodeERC20(
    data: Hex,
    selector: string,
    tokenAddress: Address,
): DecodedTransaction | null {
    try {
        const decoded = decodeFunctionData({
            abi: ERC20_ABI,
            data,
        })

        switch (selector) {
            case FUNCTION_SELECTORS.TRANSFER: {
                const [to, amount] = decoded.args as [Address, bigint]
                return {
                    type: 'erc20-transfer',
                    protocol: 'erc20',
                    action: 'Transfer',
                    description: 'Transfer tokens',
                    functionSignature: 'transfer(address,uint256)',
                    tokens: [
                        {
                            address: tokenAddress,
                            amount,
                        },
                    ],
                    recipient: to,
                    rawData: { functionName: decoded.functionName, args: decoded.args },
                }
            }

            case FUNCTION_SELECTORS.APPROVE: {
                const [spender, amount] = decoded.args as [Address, bigint]
                const warnings = []
                if (isUnlimitedApproval(amount)) {
                    warnings.push('Unlimited approval - spender can transfer all your tokens')
                }
                return {
                    type: 'erc20-approve',
                    protocol: 'erc20',
                    action: 'Approve',
                    description: isUnlimitedApproval(amount)
                        ? 'Approve unlimited spending'
                        : 'Approve token spending',
                    functionSignature: 'approve(address,uint256)',
                    tokens: [
                        {
                            address: tokenAddress,
                            amount,
                        },
                    ],
                    spender,
                    warnings,
                    rawData: { functionName: decoded.functionName, args: decoded.args },
                }
            }

            case FUNCTION_SELECTORS.TRANSFER_FROM: {
                const [from, to, amount] = decoded.args as [Address, Address, bigint]
                return {
                    type: 'erc20-transfer-from',
                    protocol: 'erc20',
                    action: 'Transfer From',
                    description: 'Transfer tokens on behalf of owner',
                    functionSignature: 'transferFrom(address,address,uint256)',
                    tokens: [
                        {
                            address: tokenAddress,
                            amount,
                        },
                    ],
                    from,
                    recipient: to,
                    rawData: { functionName: decoded.functionName, args: decoded.args },
                }
            }
        }
    } catch (error) {
        // Not an ERC20 transaction or failed to decode
    }

    return null
}

/**
 * Decode ERC721 transactions
 */
function decodeERC721(
    data: Hex,
    selector: string,
    tokenAddress: Address,
): DecodedTransaction | null {
    try {
        // First try to decode as ERC721
        const decoded = decodeFunctionData({
            abi: ERC721_ABI,
            data,
        })

        switch (selector) {
            case FUNCTION_SELECTORS.TRANSFER_FROM: {
                const [from, to, tokenId] = decoded.args as [Address, Address, bigint]
                return {
                    type: 'nft-transfer',
                    protocol: 'erc721',
                    action: 'Transfer NFT',
                    description: `Transfer NFT #${tokenId}`,
                    functionSignature: 'transferFrom(address,address,uint256)',
                    tokens: [
                        {
                            address: tokenAddress,
                            tokenId,
                        },
                    ],
                    from,
                    recipient: to,
                    rawData: { functionName: decoded.functionName, args: decoded.args },
                }
            }

            case FUNCTION_SELECTORS.SAFE_TRANSFER_FROM:
            case FUNCTION_SELECTORS.SAFE_TRANSFER_FROM_WITH_DATA: {
                const args = [...decoded.args] as [Address, Address, bigint, Hex?]
                const [from, to, tokenId, data] = args
                const hasData = selector === FUNCTION_SELECTORS.SAFE_TRANSFER_FROM_WITH_DATA
                return {
                    type: 'nft-transfer',
                    protocol: 'erc721',
                    action: 'Safe Transfer NFT',
                    description: `Safely transfer NFT #${tokenId}`,
                    functionSignature: hasData
                        ? 'safeTransferFrom(address,address,uint256,bytes)'
                        : 'safeTransferFrom(address,address,uint256)',
                    tokens: [
                        {
                            address: tokenAddress,
                            tokenId,
                        },
                    ],
                    from,
                    recipient: to,
                    data: hasData ? data : undefined,
                    rawData: { functionName: decoded.functionName, args: decoded.args },
                }
            }

            case FUNCTION_SELECTORS.APPROVE: {
                // Could be ERC721 approve
                const [to, tokenId] = decoded.args as [Address, bigint]
                // Check if tokenId looks like an NFT ID (not a huge number like ERC20 amount)
                if (tokenId < BigInt('1000000000000000000')) {
                    return {
                        type: 'nft-approve',
                        protocol: 'erc721',
                        action: 'Approve NFT',
                        description: `Approve NFT #${tokenId} transfer`,
                        functionSignature: 'approve(address,uint256)',
                        tokens: [
                            {
                                address: tokenAddress,
                                tokenId,
                            },
                        ],
                        spender: to,
                        rawData: { functionName: decoded.functionName, args: decoded.args },
                    }
                }
                break
            }

            case FUNCTION_SELECTORS.SET_APPROVAL_FOR_ALL: {
                const [operator, approved] = decoded.args as [Address, boolean]
                const warnings = approved
                    ? ['Granting permission to transfer ALL your NFTs from this collection']
                    : []
                return {
                    type: 'nft-set-approval-all',
                    protocol: 'erc721',
                    action: approved ? 'Approve All NFTs' : 'Revoke All NFTs Approval',
                    description: approved
                        ? 'Grant permission to transfer all NFTs'
                        : 'Revoke permission to transfer all NFTs',
                    functionSignature: 'setApprovalForAll(address,bool)',
                    tokens: [
                        {
                            address: tokenAddress,
                        },
                    ],
                    spender: operator,
                    warnings,
                    rawData: { functionName: decoded.functionName, args: decoded.args },
                }
            }
        }
    } catch (error) {
        // Not an ERC721 transaction or failed to decode
    }

    return null
}

/**
 * Decode ERC1155 transactions
 */
function decodeERC1155(
    data: Hex,
    selector: string,
    tokenAddress: Address,
): DecodedTransaction | null {
    try {
        const decoded = decodeFunctionData({
            abi: ERC1155_ABI,
            data,
        })

        switch (selector) {
            case FUNCTION_SELECTORS.SAFE_TRANSFER_FROM_1155: {
                const [from, to, id, amount, data] = decoded.args as [
                    Address,
                    Address,
                    bigint,
                    bigint,
                    Hex,
                ]
                return {
                    type: 'nft-transfer',
                    protocol: 'erc1155',
                    action: 'Transfer Multi-Token',
                    description: `Transfer ${amount} of token #${id}`,
                    functionSignature: 'safeTransferFrom(address,address,uint256,uint256,bytes)',
                    tokens: [
                        {
                            address: tokenAddress,
                            tokenId: id,
                            amount,
                        },
                    ],
                    from,
                    recipient: to,
                    data,
                    rawData: { functionName: decoded.functionName, args: decoded.args },
                }
            }

            case FUNCTION_SELECTORS.SAFE_BATCH_TRANSFER_FROM_1155: {
                const [from, to, ids, amounts, data] = decoded.args as [
                    Address,
                    Address,
                    bigint[],
                    bigint[],
                    Hex,
                ]
                return {
                    type: 'nft-transfer',
                    protocol: 'erc1155',
                    action: 'Batch Transfer',
                    description: `Transfer ${ids.length} different tokens`,
                    functionSignature:
                        'safeBatchTransferFrom(address,address,uint256[],uint256[],bytes)',
                    tokens: ids.map((id, i) => ({
                        address: tokenAddress,
                        tokenId: id,
                        amount: amounts[i],
                    })),
                    from,
                    recipient: to,
                    data,
                    rawData: { functionName: decoded.functionName, args: decoded.args },
                }
            }

            case FUNCTION_SELECTORS.SET_APPROVAL_FOR_ALL: {
                const [operator, approved] = decoded.args as [Address, boolean]
                const warnings = approved
                    ? ['Granting permission to transfer ALL your tokens from this collection']
                    : []
                return {
                    type: 'nft-set-approval-all',
                    protocol: 'erc1155',
                    action: approved ? 'Approve All Tokens' : 'Revoke All Tokens Approval',
                    description: approved
                        ? 'Grant permission to transfer all tokens'
                        : 'Revoke permission to transfer all tokens',
                    functionSignature: 'setApprovalForAll(address,bool)',
                    tokens: [
                        {
                            address: tokenAddress,
                        },
                    ],
                    spender: operator,
                    warnings,
                    rawData: { functionName: decoded.functionName, args: decoded.args },
                }
            }
        }
    } catch (error) {
        // Not an ERC1155 transaction
    }

    return null
}

/**
 * Main function to decode transaction data
 */
export function decodeTransaction(data: Hex, toAddress?: Address): DecodedTransaction {
    if (!data || data === '0x') {
        return {
            type: 'unknown',
            action: 'ETH Transfer',
            description: 'Native ETH transfer',
            tokens: [],
        }
    }

    const selector = getFunctionSelector(data)

    // Use a default address if toAddress is not provided
    const tokenAddress = toAddress || ('0x0' as Address)

    // Try to decode based on selector
    let decoded: DecodedTransaction | null = null

    // Try ERC20
    decoded = decodeERC20(data, selector, tokenAddress)
    if (decoded) {
        return decoded
    }

    // Try ERC721
    decoded = decodeERC721(data, selector, tokenAddress)
    if (decoded) {
        return decoded
    }

    // Try ERC1155
    decoded = decodeERC1155(data, selector, tokenAddress)
    if (decoded) {
        return decoded
    }

    // Unknown transaction
    return {
        type: 'unknown',
        action: 'Contract Interaction',
        description: 'Unknown contract function',
        tokens: [],
        rawData: { functionName: 'unknown' },
    }
}

/**
 * Get a short description for the transaction type
 */
export function getTransactionTypeLabel(type: TransactionType): string {
    switch (type) {
        case 'erc20-transfer':
            return 'Token Transfer'
        case 'erc20-approve':
            return 'Token Approval'
        case 'erc20-transfer-from':
            return 'Token Transfer (Delegated)'
        case 'nft-transfer':
            return 'NFT Transfer'
        case 'nft-approve':
            return 'NFT Approval'
        case 'nft-set-approval-all':
            return 'Collection Approval'
        default:
            return 'Contract Call'
    }
}
